import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, } from 'react';
import styled, { css } from 'styled-components';
import { Fade } from '../helpers/Fade';
import { Portal } from '../helpers/Portal';
import { Icon } from './Icon';
import { Input } from './Input/Input';
import { Button, ButtonInner } from './Button';
import { Tooltip } from './Tooltip';
const DropdownWrapper = styled.div `
  position: relative;
  display: table;

  ${({ fullWidth }) => fullWidth &&
    css `
      table-layout: fixed;
      width: 100%;
    `}

  ${({ variant }) => variant === 'dots' &&
    css `
      margin: -8px 0;
    `}

  ${({ isOpen }) => isOpen &&
    css `
      z-index: 4;
    `}
`;
const ReferenceWrapper = styled.div `
  ${({ fullWidth }) => fullWidth &&
    css `
      width: 100%;
    `}
`;
const Content = styled(Fade) `
  --animation-enter-y: 0px;
  --animation-exit-y: 0px;
  --animation-default-y: ${({ theme }) => theme.style.dropdownOffsetY};

  z-index: 99;
  min-width: var(--dropdown-min-width, ${({ theme }) => theme.style.dropdownMinWidth});
  max-width: ${({ maxWidth }) => maxWidth};

  ${({ positionTop }) => positionTop &&
    css `
      --animation-default-y: calc(${({ theme }) => theme.style.dropdownOffsetY} * -1);
    `}

  ${({ fullWidth }) => fullWidth &&
    css `
      min-width: 100%;
      box-sizing: border-box;
    `}

  ${({ isInside }) => isInside &&
    css `
      padding-top: 0;
    `}

  @keyframes content {
    from {
      transform: scaleY(0.9);
    }
  }

  &::before,
  &:after {
    content: '';
  }

  &:before {
    display: table;
  }

  &:after {
    position: absolute;
    inset: ${({ theme }) => theme.style.buttonSecondaryBackgroundInset};
    background-color: ${({ theme }) => theme.style.dropdownBackgroundColor};
    border-radius: ${({ theme }) => theme.style.dropdownBorderRadius};
    transform-origin: 50% 0;
    box-shadow:
      ${({ theme }) => theme.style.dropdownBorderProperties} ${({ theme }) => theme.style.dropdownBorderWidth}
        ${({ theme }) => theme.style.dropdownBorderColor},
      ${({ theme }) => theme.style.floatingShadow1PropertiesM} ${({ theme }) => theme.style.floatingShadow1ColorM},
      ${({ theme }) => theme.style.floatingShadow2PropertiesM} ${({ theme }) => theme.style.floatingShadow2ColorM};

    ${({ positionTop }) => positionTop &&
    css `
        transform-origin: 50% 100%;
      `}

    ${({ isOpen }) => isOpen &&
    css `
        animation: content 0.25s;
      `};

    ${({ isInside }) => isInside &&
    css `
        top: -40px;

        ${({ positionTop }) => positionTop &&
        css `
            top: 0;
            bottom: -40px;
          `}
      `}
  }

  ${({ isInside }) => isInside &&
    css `
      --animation-default-y: 0px;
    `};
`;
const ContentInner = styled.div `
  padding: calc(
      ${({ theme }) => theme.style.dropdownPaddingY} + ${({ theme }) => theme.style.buttonSecondaryBackgroundInset}
    )
    calc(${({ theme }) => theme.style.dropdownPaddingX} + ${({ theme }) => theme.style.buttonSecondaryBackgroundInset});
  overflow-y: scroll;
  border-radius: ${({ theme }) => theme.style.dropdownBorderRadius};
  -ms-overflow-style: none;
  scrollbar-width: none;
  position: relative;
  z-index: 1;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  gap: 1px;

  ${({ maxHeight }) => maxHeight &&
    css `
      max-height: ${maxHeight};
    `}

  &::-webkit-scrollbar {
    display: none;
  }
`;
const Label = styled(Button) `
  --button-scale-pressed: 1;
  --button-align: space-between;
  --dropdown-border-color: ${({ hasError, theme }) => hasError ? theme.style.inputErrorTextColor : theme.style.buttonSecondaryBorderColor};
  font-weight: 500;
  position: relative;
  z-index: 3;
  box-sizing: border-box;
  font-weight: ${({ theme }) => theme.style.dropdownButtonFontWeight};

  ${({ hasIcon, size }) => hasIcon &&
    css `
      --button-padding-x-sm: 8px;
      padding-top: ${size === 'small' ? '7px' : '10px'};
      padding-bottom: ${size === 'small' ? '7px' : '10px'};

      svg {
        --icon-size: ${size === 'small' ? '16px' : '20px'};
      }
    `}

  &:before {
    content: '';
    background-image: linear-gradient(
      to bottom,
      ${({ theme }) => theme.id === 'dark'
    ? `${theme.secondary.blendToBackground(25)}, ${theme.background}`
    : `${theme.background}, ${theme.secondary.blendToBackground(25)}`}
    );
    transition: opacity 0.2s ease-in-out;
    inset: 0;
    position: absolute;
    pointer-events: none;
    border-radius: ${({ theme }) => theme.style.buttonBorderRadius};
  }

  &:after {
    pointer-events: none;
    background: transparent;
    box-shadow:
      0 -1px 1px
        color-mix(
          in srgb,
          ${({ theme }) => (theme.id === 'dark' ? `${theme.secondary.blendToBackground(300)}` : `${theme.background}`)}
            75%,
          transparent
        )
        inset,
      ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
        ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
        ${({ theme }) => theme.style.buttonSecondaryBorderColor},
      0 1px 1px rgba(0, 0, 0, 0.075),
      0 1px 4px 0 transparent inset,
      0 1px 2px rgba(0, 0, 0, 0.1);
    transition: box-shadow 0.2s ease-in-out;
  }

  &:hover {
    &:before {
      opacity: 0;
    }
    &:after {
      box-shadow:
        0 -1px 1px color-mix(in srgb, ${({ theme }) => theme.background} 75%, transparent) inset,
        ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
          ${({ theme }) => theme.style.buttonSecondaryBorderWidth}
          ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
        0 1px 1px rgba(0, 0, 0, 0.075),
        0 1px 4px 0 transparent inset,
        0 1px 2px rgba(0, 0, 0, 0.1);
    }
  }

  &:active,
  &:focus-visible {
    &:after {
      box-shadow:
        0 -1px 1px color-mix(in srgb, ${({ theme }) => theme.background} 75%, transparent) inset,
        ${({ theme }) => theme.style.buttonSecondaryHoveredBorderProperties}
          ${({ theme }) => theme.style.buttonSecondaryHoveredBorderWidth}
          ${({ theme }) => theme.secondary.blendToBackground(1100, 150)},
        0 1px 1px rgba(0, 0, 0, 0),
        0 1px 4px 0 ${({ theme }) => theme.secondary.blendToBackground(120)} inset;
    }
  }

  ${({ variant }) => variant === 'muted' &&
    css `
      &:after {
        content: '';
        position: absolute;
        inset: ${({ theme }) => theme.style.buttonSecondaryBackgroundInset};
        background-color: ${({ theme }) => theme.style.buttonSecondaryBackgroundColor};
        --dropdown-border-color: ${({ theme }) => theme.secondary.blendToBackground(200)};
        background-image: transparent;
        box-shadow:
          ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
            ${({ theme }) => theme.style.buttonSecondaryBorderWidth} var(--dropdown-border-color),
          0 0 0 var(--button-outline, 0px)
            var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor});
      }

      &:hover {
        &:after {
          background-color: ${({ theme }) => theme.style.buttonSecondaryHoveredBackgroundColor};
          --dropdown-border-color: ${({ theme }) => theme.secondary.blendToBackground(300)};
          box-shadow:
            ${({ theme }) => theme.style.buttonSecondaryBorderProperties}
              ${({ theme }) => theme.style.buttonSecondaryBorderWidth} var(--dropdown-border-color),
            0 0 0 var(--button-outline, 0px)
              var(--button-outline-color, ${({ theme }) => theme.style.buttonFocussedOutlineColor});
        }
      }
    `};

  &:focus-visible {
    --dropdown-border-color: ${({ theme }) => theme.style.inputFocussedBorderColor};
  }

  ${ButtonInner} {
    gap: 8px;
  }

  ${({ size }) => size === 'small' &&
    css `
      ${ButtonInner} {
        gap: 4px;
      }
    `}
`;
const Placeholder = styled.span `
  color: ${({ hasError, theme }) => hasError ? theme.style.dropdownErrorPlaceholderColor : theme.style.dropdownPlaceholderColor};
`;
const StyledTooltip = styled(Tooltip) `
  width: 100%;
  max-width: 100%;
`;
const DropdownEntryTooltip = ({ tooltip, children }) => {
    return (!!tooltip ? (React.createElement(StyledTooltip, { zIndex: 16, ...tooltip }, children)) : (children));
};
export const DropdownEntry = styled.button `
  appearance: none;
  outline: none;
  border: none;
  display: flex;
  align-items: center;
  white-space: nowrap;
  width: 100%;
  box-sizing: border-box;
  gap: ${({ theme }) => theme.style.dropdownEntryGap};
  position: relative;
  z-index: 1;
  cursor: pointer;
  padding: ${({ theme }) => theme.style.dropdownEntryPadding};
  line-height: ${({ theme }) => theme.style.dropdownEntryLineHeight};
  border-radius: ${({ theme }) => theme.style.dropdownEntryBorderRadius};
  color: var(--entry-color, ${({ theme }) => theme.style.dropdownEntryColor});
  background-color: var(--entry-background, ${({ theme }) => theme.style.dropdownEntryBackgroundColor});
  box-shadow: inset 0 0 0 var(--entry-border, ${({ theme }) => theme.style.dropdownEntryBorder})
    var(--entry-border-color, ${({ theme }) => theme.style.dropdownEntryBorderColor});
  transition:
    background-color 0.1s,
    color 0.1s,
    box-shadow 0.1s;
  margin: ${({ theme }) => theme.style.dropdownEntryMargin};

  &:first-child {
    margin-top: ${({ theme }) => theme.style.dropdownEntryFirstLastMargin};
  }

  &:last-child {
    margin-bottom: ${({ theme }) => theme.style.dropdownEntryFirstLastMargin};
  }

  svg {
    --icon-size: ${({ theme }) => theme.style.dropdownEntryIconSize};
    --icon-color: ${({ theme }) => theme.style.dropdownEntryIconColor};
  }

  ${({ size }) => size === 'small' &&
    css `
      padding: ${({ theme }) => theme.style.dropdownEntryPaddingSmall};
    `}

  ${({ variant }) => variant === 'secondary' &&
    css `
      color: var(--entry-secondary-color, ${({ theme }) => theme.style.dropdownEntrySecondaryColor});

      svg {
        --icon-color: ${({ theme }) => theme.style.dropdownEntrySecondaryIconColor};
      }
    `}

  ${({ isDisabled }) => isDisabled &&
    css `
      opacity: 0.75;
      cursor: not-allowed;
    `}

  ${({ isDisabled }) => !isDisabled &&
    css `
      &:hover {
        --entry-background: ${({ theme }) => theme.style.dropdownEntryHoveredBackgroundColor};
        --entry-border: ${({ theme }) => theme.style.dropdownEntryHoveredBorder};
        --entry-border-color: ${({ theme }) => theme.style.dropdownEntryHoveredBorderColor};
        --entry-color: ${({ theme }) => theme.style.dropdownEntryHoveredColor};
      }
    `}

  ${({ isActive, size }) => isActive &&
    css `
      padding: ${({ theme }) => size === 'small' ? theme.style.dropdownEntryActivePaddingSmall : theme.style.dropdownEntryActivePadding};
      --entry-background: ${({ theme }) => theme.style.dropdownEntryActiveBackgroundColor};
      --entry-border: ${({ theme }) => theme.style.dropdownEntryActiveBorder};
      --entry-border-color: ${({ theme }) => theme.style.dropdownEntryActiveBorderColor};
      --entry-color: ${({ theme }) => theme.style.dropdownEntryActiveColor};

      &:hover {
        --entry-background: ${({ theme }) => theme.style.dropdownEntryActiveHoveredBackgroundColor};
        --entry-border: ${({ theme }) => theme.style.dropdownEntryActiveHoveredBorder};
        --entry-border-color: ${({ theme }) => theme.style.dropdownEntryActiveHoveredBorderColor};
        --entry-color: ${({ theme }) => theme.style.dropdownEntryActiveHoveredColor};
      }
    `}

  ${({ isDanger }) => isDanger &&
    css `
      --entry-background: ${({ theme }) => theme.style.dropdownEntryDangerBackgroundColor};
      --entry-border: ${({ theme }) => theme.style.dropdownEntryDangerBorder};
      --entry-border-color: ${({ theme }) => theme.style.dropdownEntryDangerBorderColor};
      --entry-color: ${({ theme }) => theme.style.dropdownEntryDangerColor};

      &:hover {
        --entry-background: ${({ theme }) => theme.style.dropdownEntryDangerHoveredBackgroundColor};
        --entry-border: ${({ theme }) => theme.style.dropdownEntryDangerHoveredBorder};
        --entry-border-color: ${({ theme }) => theme.style.dropdownEntryDangerHoveredBorderColor};
        --entry-color: ${({ theme }) => theme.style.dropdownEntryDangerHoveredColor};
      }
    `}

  ${({ isLoading }) => isLoading &&
    css `
      color: ${({ theme }) => theme.secondary.blendToBackground(800)};
      justify-content: center;
      pointer-events: none;
    `}
`;
const DropdownEntryContentWrapper = styled.div `
  display: flex;
  align-items: center;
  width: 100%;
`;
export const DropdownEntryIcon = styled.div `
  padding-right: 4px;
`;
export const DropdownEntryText = styled.div `
  font-family: inherit;
  font-size: ${({ theme }) => theme.style.dropdownEntryFontSize};
  font-weight: ${({ theme }) => theme.style.dropdownEntryFontWeight};
  text-align: left;
  box-sizing: border-box;
  width: 100%;

  ${({ isLabel }) => isLabel &&
    css `
      max-width: 100%;
      overflow: hidden;
      text-overflow: ellipsis;
    `}
`;
const DropdownEntryTitle = styled.div `
  flex-grow: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  width: 100%;
`;
const DropdownEntryTitleLabel = styled.div `
  flex-grow: 1;
  display: table-cell;
  overflow: hidden;
  max-width: 100%;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const DropdownEntrySuffix = styled.div `
  max-height: 16px;
  line-height: ${({ theme }) => theme.style.dropdownEntryLineHeight};
`;
const EntrySmall = styled.small `
  font-size: ${({ theme }) => theme.style.dropdownEntrySmallFontSize};
  font-weight: 400;
  display: block;
  max-width: 100%;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  margin-top: 4px;
  color: ${({ theme }) => theme.style.dropdownEntrySmallColor};
  line-height: ${({ theme }) => theme.style.dropdownEntrySmallLineHeight};
`;
const Empty = styled.div `
  color: ${({ theme }) => theme.secondary.blendToBackground(600)};
  position: relative;
  z-index: 1;
  padding: 8px 4px 16px;
  font-size: 12px;
  text-align: center;
`;
const ArrowStyles = css `
  --icon-color: ${({ theme }) => theme.style.dropdownArrowColor};
  transition: transform 0.2s;

  ${({ isOpen }) => isOpen &&
    css `
      transform: scaleY(-1);
    `}
`;
const Arrow = styled(Icon.ChevronDown) `
  ${ArrowStyles}
  --icon-size: 20px;
`;
const ArrowSmall = styled(Icon.ChevronDown) `
  ${ArrowStyles}
`;
export const DropdownCheckmark = styled(Icon.Checkmark) `
  --icon-color: ${({ theme }) => theme.foreground};
  --icon-size: 16px;
  position: absolute;
  right: ${({ theme }) => theme.style.dropdownEntryActiveCheckmarkRight};
  top: calc(12px + ${({ theme }) => theme.style.dropdownEntryLineHeight} - 16px);
  display: ${({ theme }) => theme.style.dropdownEntryActiveCheckmarkDisplay};

  ${({ size }) => size === 'small' &&
    css `
      top: calc(8px + ${({ theme }) => theme.style.dropdownEntryLineHeight} - 16px);
    `}
`;
const Dots = styled(Icon.Dots) `
  --icon-color: ${({ theme }) => theme.secondary.background};
  --icon-size: 24px;
  margin-left: auto;
  transition: transform 0.2s;
  cursor: pointer;
  position: relative;
  user-select: none;
  z-index: 3;
  padding: 8px;

  ${({ isOpen }) => isOpen &&
    css `
      transform: rotate(90deg);
    `}
`;
const Custom = styled.div `
  padding: var(--dropdown-custom-padding, 0 0 0 0);
  cursor: pointer;
  line-height: 16px;

  ${({ fullWidth }) => fullWidth &&
    css `
      width: 100%;
    `}
`;
const Search = styled(Input) `
  z-index: 1;
  margin: calc(${({ theme }) => theme.style.dropdownPaddingX} + ${({ theme }) => theme.style.formElementOutlineWidth})
    calc(${({ theme }) => theme.style.dropdownPaddingX} + ${({ theme }) => theme.style.formElementOutlineWidth}) 0
    calc(${({ theme }) => theme.style.dropdownPaddingX} + ${({ theme }) => theme.style.formElementOutlineWidth});
  min-width: 120px;
`;
const DropdownContent = styled.div `
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 2;
`;
const RelativeLabel = styled.div `
  position: absolute;
  z-index: 100;
  top: ${({ labelHeight }) => `-${labelHeight}px`};
  pointer-events: none;

  ${({ positionRight }) => positionRight &&
    css `
      right: 0;
    `}

  ${({ positionTop }) => positionTop &&
    css `
      top: auto;
      bottom: ${({ labelHeight }) => `-${labelHeight}px`};
    `}

  ${({ show }) => !show &&
    css `
      opacity: 0;
    `}
`;
const Divider = styled.div `
  margin: 8px -8px;
  height: 1px;
  background-color: ${({ theme }) => theme.secondary.blendToBackground(150)};

  ${({ size }) => size === 'small' &&
    css `
      margin: 4px -8px;
    `}

  ${({ title }) => title &&
    css `
      margin-bottom: 36px;
      position: relative;

      &:first-child {
        background-color: transparent;
      }

      &:after {
        content: '${title}';
        display: block;
        position: absolute;
        padding: ${({ theme }) => theme.style.dropdownEntryPadding};
        padding-bottom: 0;
        left: 8px;
        top: 0;
        font: inherit;
        font-weight: 500;
        font-size: 12px;
        line-height: 16px;
        color: ${({ theme }) => theme.secondary.blendToBackground(700)};
      }
    `}
`;
const EntryContent = ({ entry, showSmall, isLabel, }) => {
    if (entry.length === 1) {
        return (React.createElement(DropdownEntryContentWrapper, null,
            entry[0].icon && React.createElement(DropdownEntryIcon, null, entry[0].icon),
            React.createElement(DropdownEntryText, { isLabel: isLabel ?? false },
                isLabel ? (entry[0].label) : (React.createElement(DropdownEntryTitle, null,
                    React.createElement(DropdownEntryTitleLabel, null, entry[0].label),
                    entry[0]?.suffix && React.createElement(DropdownEntrySuffix, null, entry[0].suffix))),
                showSmall && entry[0]?.small && React.createElement(EntrySmall, null, entry[0].small))));
    }
    return React.createElement(DropdownEntryText, { isLabel: isLabel ?? false }, entry.map((e) => e.label).join(', '));
};
export const Dropdown = forwardRef((props, ref) => {
    const contentInnerRef = useRef(null);
    const referenceRef = useRef(null);
    const dropdownWrapperRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [searchValue, setSearchValue] = useState('');
    const [placement, setPlacement] = useState('bottom-start');
    const [current, setCurrent] = useState(props.active);
    const styleProps = {
        isOpen: props.isOpen ?? isOpen,
        isInside: props.isInside ?? false,
        isDisabled: props.isDisabled ?? false,
        isLoading: props.isLoading ?? false,
        variant: props.variant ?? 'secondary',
        size: props.size ?? 'default',
        positionRight: props.positionRight ?? false,
        positionTop: placement === 'top-start' || placement === 'top-end',
        fullWidth: props.fullWidth ?? false,
        hasError: props.hasError ?? false,
        maxWidth: props.maxWidth ?? '100%',
        maxHeight: props.maxHeight ?? '248px',
        offsetX: props.offsetX ?? 0,
        offsetY: props.offsetY ?? 0,
    };
    const handleClick = (event) => {
        event.stopPropagation();
        if (props.isDisabled || props.isLoading) {
            return;
        }
        if (props.onClick) {
            props.onClick(event);
        }
        if (props.onOpenChange) {
            props.onOpenChange(!isOpen);
        }
        setIsOpen(!isOpen);
    };
    const handleOutClick = (event) => {
        if (event.target === dropdownWrapperRef.current ||
            dropdownWrapperRef.current?.contains(event.target)) {
            return;
        }
        if (!isOpen) {
            return;
        }
        if (props.onOpenChange) {
            props.onOpenChange(false);
        }
        setIsOpen(false);
    };
    const handleElementClick = useCallback((event, entry) => {
        event.stopPropagation();
        if (entry.isDisabled) {
            return;
        }
        const isMultiple = Array.isArray(current);
        const isActive = isMultiple ? current.includes(entry.value) : entry.value === current;
        const newValue = isMultiple
            ? isActive
                ? current.filter((v) => v !== entry.value)
                : [...current, entry.value]
            : entry.value;
        if (props.onChange) {
            if (!isMultiple && isActive) {
                setCurrent(undefined);
                props.onChange(undefined);
                return;
            }
            props.onChange(newValue);
        }
        if (entry.onClick) {
            entry.onClick(event);
        }
        if (props.onOpenChange) {
            props.onOpenChange(false);
        }
        if (!props.preventSelect) {
            setCurrent(newValue === current ? undefined : newValue);
        }
        setIsOpen(false);
    }, [props.preventSelect, current, props.onChange, props.onOpenChange]);
    const handleElementHover = useCallback((value, event, entry) => {
        if (entry.isDisabled) {
            return;
        }
        if (entry.onHover) {
            entry.onHover(value, event);
        }
    }, []);
    const handleSearchChange = (value) => {
        setSearchTerm(value);
        if (props.onSearchChange) {
            props.onSearchChange(value);
        }
        else {
            setSearchValue(value);
        }
    };
    const handleScroll = () => {
        if (!contentInnerRef.current) {
            return;
        }
        const contentInner = contentInnerRef.current;
        if (contentInner.offsetHeight + contentInner.scrollTop >= contentInner.scrollHeight) {
            if (props.onScrollEnd) {
                props.onScrollEnd();
            }
        }
    };
    const handleFilterOptions = (entry) => {
        if (!props.search || !searchValue || entry.isDivider) {
            return true;
        }
        const label = typeof entry.label === 'string' ? entry.label : '';
        const search = searchValue.toLowerCase();
        if (entry.value) {
            return label.toLowerCase().includes(search) || entry.value.toString().toLowerCase().includes(search);
        }
        return label.toLowerCase().includes(search);
    };
    const clearSearch = useCallback(() => {
        setSearchTerm('');
        setSearchValue('');
    }, []);
    const close = useCallback(() => {
        setIsOpen(false);
        if (props.onOpenChange) {
            props.onOpenChange(false);
        }
    }, [props.onOpenChange]);
    const open = useCallback(() => {
        setIsOpen(true);
        if (props.onOpenChange) {
            props.onOpenChange(true);
        }
    }, [props.onOpenChange]);
    const setSearch = useCallback((value) => {
        setSearchTerm(value ?? '');
        setSearchValue(value ?? '');
    }, []);
    useImperativeHandle(ref, () => ({
        clearSearch,
        close,
        open,
        setSearch,
    }), []);
    useLayoutEffect(() => {
        if (typeof props.isOpen === 'undefined') {
            return;
        }
        setIsOpen(props.isOpen);
    }, [props.isOpen]);
    useEffect(() => {
        setCurrent(props.active);
    }, [props.active]);
    useEffect(() => {
        if (!contentInnerRef.current) {
            return;
        }
        const contentInner = contentInnerRef.current;
        contentInner.addEventListener('scroll', handleScroll);
        return () => {
            contentInner.removeEventListener('scroll', handleScroll);
        };
    });
    const allOptions = useMemo(() => {
        const seen = new Set();
        return [...(props.hiddenOptions ?? []), ...props.options].filter((o) => {
            if (o.value === undefined || !seen.has(o.value)) {
                if (o.value !== undefined)
                    seen.add(o.value);
                return true;
            }
            return false;
        });
    }, [props.hiddenOptions, props.options]);
    const selectedOption = useMemo(() => allOptions.filter((e) => (Array.isArray(current) ? current.includes(e.value) : e.value === current)), [current, allOptions]);
    return (React.createElement(DropdownWrapper, { className: props.className, ref: dropdownWrapperRef, ...styleProps },
        React.createElement(ReferenceWrapper, { ref: referenceRef, fullWidth: styleProps.fullWidth }, props.customLabel ? (React.createElement(Custom, { onClick: handleClick, role: "button", fullWidth: styleProps.fullWidth }, props.customLabel)) : props.variant === 'dots' ? (React.createElement(Dots, { onClick: handleClick, isOpen: isOpen })) : (React.createElement(Label, { onClick: handleClick, ...styleProps, variant: styleProps.variant !== 'dots' ? styleProps.variant : undefined, type: "button", hasIcon: selectedOption && selectedOption[0] && !!selectedOption[0].icon, icon: styleProps.size === 'small' ? React.createElement(ArrowSmall, { ...styleProps }) : React.createElement(Arrow, { ...styleProps }), hasError: styleProps.hasError, iconRight: true }, props.label ? (React.createElement(EntryContent, { entry: [{ label: props.label }], isLabel: true })) : (React.createElement(React.Fragment, null, selectedOption && selectedOption[0] ? (React.createElement(EntryContent, { entry: selectedOption, isLabel: true })) : (React.createElement(Placeholder, { ...styleProps }, props.placeholder ?? 'Please select'))))))),
        React.createElement(Portal, { reference: referenceRef, fullWidth: props.fullWidth, onPlacementChange: (p) => setPlacement(p), placement: props.positionRight ? 'bottom-end' : 'bottom-start', offsetCross: styleProps.offsetX, offsetMain: styleProps.offsetY, restrictFlipCross: true, onOutClick: handleOutClick, renderInside: props.renderInside, zIndex: 15, shouldCalculate: { is: isOpen, timeout: 200 } },
            props.isInside && (React.createElement(RelativeLabel, { show: isOpen, labelHeight: referenceRef.current?.offsetHeight ?? 0, positionRight: styleProps.positionRight, positionTop: styleProps.positionTop }, props.customLabel ? (React.createElement(Custom, { role: "button", fullWidth: styleProps.fullWidth }, props.customLabel)) : props.variant === 'dots' ? (React.createElement(Dots, { isOpen: isOpen })) : (React.createElement(Label, { ...styleProps, variant: styleProps.variant !== 'dots' ? styleProps.variant : undefined, type: "button" },
                props.label ? (React.createElement(EntryContent, { entry: [{ label: props.label }], isLabel: true })) : (React.createElement(React.Fragment, null, selectedOption ? (React.createElement(EntryContent, { entry: selectedOption, isLabel: true })) : (React.createElement(Placeholder, { ...styleProps }, props.placeholder ?? 'Please select')))),
                React.createElement(Arrow, { ...styleProps }))))),
            React.createElement(Content, { show: props.isOpen ?? isOpen, timeoutEnter: 0, ...styleProps },
                props.search && (React.createElement(Search, { placeholder: props.searchLabel ?? 'Search', value: searchTerm, onChange: handleSearchChange, icon: React.createElement(Icon.Search, null), size: "small", autoFocus: true })),
                React.createElement(ContentInner, { ...styleProps, ref: contentInnerRef }, props.options.filter(handleFilterOptions).map((entry, index) => {
                    if (entry.isDivider) {
                        const before = props.options.filter(handleFilterOptions).find((__, i) => i < index);
                        const after = props.options.filter(handleFilterOptions).find((__, i) => i > index);
                        if (entry.label) {
                            return React.createElement(Divider, { key: index, size: props.size, title: entry.label });
                        }
                        if (!before || !after) {
                            return null;
                        }
                        return React.createElement(Divider, { key: index, size: props.size });
                    }
                    const isActive = Array.isArray(current)
                        ? current.includes(entry.value)
                        : typeof current !== 'undefined' && current === entry.value;
                    return (React.createElement(DropdownEntryTooltip, { key: index, tooltip: entry.tooltip },
                        React.createElement(DropdownEntry, { "data-cy": `dropdown-option-${entry.value}`, onClick: (event) => handleElementClick(event, entry), onMouseEnter: (event) => handleElementHover(true, event, entry), onMouseLeave: (event) => handleElementHover(false, event, entry), isDanger: entry.isDanger, isDisabled: entry.isDisabled, isLoading: entry.isLoading, key: index, size: props.size, type: "button", variant: entry.variant, isActive: isActive },
                            React.createElement(EntryContent, { entry: [entry], showSmall: true }),
                            isActive && React.createElement(DropdownCheckmark, { size: props.size }))));
                })),
                props.search &&
                    !props.isLoading &&
                    searchValue &&
                    props.options.length > 0 &&
                    props.options.filter(handleFilterOptions).filter((e) => !e.isDivider).length < 1 && (React.createElement(Empty, null, "Nothing found")),
                props.search && !props.isLoading && props.options.length < 1 && (React.createElement(Empty, null, props.emptyMessage ?? 'No entries')),
                props.renderContent && React.createElement(DropdownContent, null, props.renderContent)))));
});
//# sourceMappingURL=Dropdown.js.map